(function () {
  let swatchTooltipSequence = 0;

  class TiyBundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        // The same node can be re-inserted (section re-render, theme editor block
        // move) after disconnectedCallback removed the tooltip wiring. Restore
        // only that wiring: quantities, rendered rows, and the existing
        // click/keydown listeners are left exactly as they were.
        this.setupSwatchTooltips();
        this.resumeCatalogueLoad();
        return;
      }
      this.initialized = true;
      this.maxItems = Number(this.dataset.maxItems) || 20;
      this.configureTierColors();
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.basePrice = Number(this.dataset.basePrice) || 0;
      this.baseComparePrice = Number(this.dataset.baseComparePrice) || 0;
      this.flexBundleVariantId = Number(this.dataset.flexBundleVariantId) || 0;
      // A required quantity switches the builder to a fixed bundle: exactly
      // that many refills (plus an optional included product), priced by the
      // Flex bundle parent instead of the tiered Component Sum.
      this.requiredQuantity = Number(this.dataset.requiredQuantity) || 0;
      this.isFixed = this.requiredQuantity > 0;
      this.minItems = this.isFixed ? this.requiredQuantity : 5;
      this.includedVariantId = Number(this.dataset.includedVariantId) || 0;
      this.includedTitle = this.dataset.includedTitle || '';
      this.includedImage = this.dataset.includedImage || '';
      this.isSubmitting = false;
      this.addedSignature = '';
      this.cta = this.querySelector('[data-flex-bundle-cta]');
      this.cartMessage = this.querySelector('[data-cart-message]');
      this.swatches = Array.from(this.querySelectorAll('[data-swatch]'));
      this.rows = Array.from(this.querySelectorAll('[data-selected-row]'));
      this.quantities = new Map(
        this.swatches.map((swatch) => [swatch.dataset.itemKey, Number(swatch.dataset.quantity) || 0])
      );
      this.initialiseCatalogue();

      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('keydown', this.handleKeydown.bind(this));
      this.querySelectorAll('[data-drag-scroll]').forEach((rail) => this.enableDragScroll(rail));
      this.scrollCueUpdates = [];
      this.scrollCueObservers = [];
      this.setupScrollCue(this.querySelector('.tiy-bundle__swatches'), 'horizontal');
      this.setupScrollCue(this.querySelector('[data-selection-list]'), 'vertical');
      this.setupSwatchTooltips();
      const initialTab = this.querySelector('[data-family-tab][aria-selected="true"]') || this.querySelector('[data-family-tab]');
      if (initialTab) this.selectFamily(initialTab, false);
      this.render(false);
      // Collection mode renders swatches without server-side family tallies, so
      // the counts are always derived from what is actually on the page.
      if (this.catalogueMode) this.refreshCatalogueCounts();
      this.syncCatalogueFeedback();
      this.resumeCatalogueLoad();
    }

    disconnectedCallback() {
      this.teardownSwatchTooltips();
      this.abortCatalogueLoad();
    }

    // Colors for the existing 5/10/15/20 tiers, in order. Tier quantities and
    // discount percentages stay hard-coded in this file, so the fill, the
    // discount math, and the reached labels keep their current behavior.
    configureTierColors() {
      const defaults = ['#e8b6d3', '#8d70ad', '#608db9', '#6a9e70'];
      let configured = null;
      if (this.dataset.tierColors) {
        try {
          const parsed = JSON.parse(this.dataset.tierColors);
          if (Array.isArray(parsed)) configured = parsed;
        } catch (error) {
          // Unreadable markup falls back to the default palette.
        }
      }
      this.tierColors = defaults.map((fallback, index) => {
        const value = configured && typeof configured[index] === 'string' ? configured[index].trim() : '';
        return value || fallback;
      });
    }

    handleClick(event) {
      if (event.target.closest('[data-catalogue-more]')) {
        this.loadCataloguePages();
        return;
      }

      const thumbnail = event.target.closest('[data-thumbnail]');
      if (thumbnail) {
        this.selectMedia(thumbnail.dataset.thumbnail);
        return;
      }

      const familyTab = event.target.closest('[data-family-tab]');
      if (familyTab) {
        this.selectFamily(familyTab);
        return;
      }

      const action = event.target.closest('[data-action]');
      if (action) {
        this.updateQuantity(action.dataset.itemKey, action.dataset.action);
        return;
      }

      const swatch = event.target.closest('[data-swatch]');
      if (swatch && !swatch.disabled) {
        this.updateQuantity(swatch.dataset.itemKey, 'increment');
        return;
      }

      if (event.target.closest('[data-flex-bundle-cta]')) {
        const state = this.getState();
        const submitEvent = new CustomEvent('tiy:bundle-submit', {
          bubbles: true,
          cancelable: true,
          detail: state
        });
        if (this.dispatchEvent(submitEvent)) this.addToCart(state);
      }
    }

    handleKeydown(event) {
      const tab = event.target.closest('[data-family-tab]');
      if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

      event.preventDefault();
      const tabs = Array.from(this.querySelectorAll('[data-family-tab]'));
      const currentIndex = tabs.indexOf(tab);
      let nextIndex = currentIndex;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = tabs.length - 1;
      tabs[nextIndex].focus();
      this.selectFamily(tabs[nextIndex]);
    }

    selectMedia(index) {
      this.querySelectorAll('[data-media-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.mediaPanel !== String(index);
      });
      this.querySelectorAll('[data-thumbnail]').forEach((thumbnail) => {
        const active = thumbnail.dataset.thumbnail === String(index);
        thumbnail.classList.toggle('is-active', active);
        thumbnail.setAttribute('aria-pressed', String(active));
      });
    }

    selectFamily(selectedTab, shouldScroll = true) {
      const family = selectedTab.dataset.familyTab;
      this.querySelectorAll('[data-family-tab]').forEach((tab) => {
        const active = tab === selectedTab;
        const familyName = tab.dataset.familyName || tab.textContent.trim();
        const familyCount = Number(tab.dataset.familyCount) || 0;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        tab.textContent = active && familyCount > 0
          ? `${familyName} (${familyCount})`
          : familyName;
      });
      this.swatches.forEach((swatch) => {
        swatch.hidden = family !== 'all' && swatch.dataset.family !== family;
      });
      // A filter change can hide the swatch that owns the tooltip, so drop it
      // instead of leaving a stale name floating over the new family.
      this.hideSwatchTooltip();
      if (shouldScroll) {
        selectedTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        const swatchRail = this.querySelector('.tiy-bundle__swatches');
        if (swatchRail) swatchRail.scrollTo({ left: 0, behavior: 'smooth' });
      }
      this.updateScrollCues();
    }

    updateQuantity(itemKey, action) {
      const total = this.getTotalQuantity();
      const current = this.quantities.get(itemKey) || 0;
      let next = current;

      if (action === 'increment' && total < this.maxItems) next += 1;
      if (action === 'decrement') next = Math.max(0, next - 1);
      if (action === 'remove') next = 0;
      if (next === current) return;

      this.quantities.set(itemKey, next);
      this.clearAddedState();
      this.render(true);
    }

    // The "Added to cart" confirmation is temporary: it clears when the
    // selection changes or after a few seconds, so the CTA returns to
    // "Add to cart" and the same bundle can be added again.
    clearAddedState() {
      window.clearTimeout(this.addedTimer);
      this.addedSignature = '';
      this.setCartMessage('');
    }

    render(announce) {
      const state = this.getState();
      const atMaximum = state.quantity >= this.maxItems;

      this.swatches.forEach((swatch) => {
        const quantity = this.quantities.get(swatch.dataset.itemKey) || 0;
        const badge = swatch.querySelector('[data-swatch-count]');
        swatch.dataset.quantity = String(quantity);
        swatch.classList.toggle('is-selected', quantity > 0);
        swatch.classList.toggle('is-maxed', atMaximum);
        swatch.setAttribute('aria-pressed', String(quantity > 0));
        if (badge) {
          badge.textContent = quantity;
          badge.hidden = quantity === 0;
        }
      });

      this.rows.forEach((row) => {
        const quantity = this.quantities.get(row.dataset.selectedRow) || 0;
        row.hidden = quantity === 0;
        const output = row.querySelector('[data-row-quantity]');
        const increment = row.querySelector('[data-action="increment"]');
        if (output) output.textContent = quantity;
        if (increment) increment.disabled = atMaximum;
      });

      const empty = this.querySelector('[data-empty-state]');
      if (empty) empty.hidden = state.quantity > 0;

      this.renderPrice(state);
      this.renderCta(state);
      this.updateScrollCues();

      if (announce) {
        this.dispatchEvent(new CustomEvent('tiy:bundle-change', {
          bubbles: true,
          detail: state
        }));
      }
    }

    renderPrice(state) {
      const original = this.querySelector('[data-price-original]');
      const current = this.querySelector('[data-price-current]');
      if (!original || !current) return;

      if (state.quantity === 0 || this.isFixed) {
        original.hidden = !(this.baseComparePrice > this.basePrice);
        original.textContent = this.formatMoney(this.baseComparePrice);
        current.textContent = this.formatMoney(this.basePrice);
        return;
      }

      original.hidden = state.discount === 0;
      original.textContent = this.formatMoney(state.subtotal);
      current.textContent = this.formatMoney(state.total);
    }

    renderCta(state) {
      const text = this.querySelector('[data-cta-text]');
      const progress = this.querySelector('[data-progress-bar]');
      const progressTrack = this.querySelector('[data-progress-track]');
      const progressMessage = this.querySelector('[data-progress-message]');
      const discountBadge = this.querySelector('[data-cta-discount]');
      const liveStatus = this.querySelector('[data-live-status]');
      const eligible = state.quantity >= this.minItems;
      const tiers = [5, 10, 15, 20];
      const nextTier = tiers.find((tier) => state.quantity < tier);
      const remaining = nextTier ? nextTier - state.quantity : 0;
      let message;
      let tierMessage;

      if (this.isSubmitting) {
        message = `Adding ${state.quantity} ${this.pluralize(state.quantity).toLowerCase()} to cart…`;
      } else if (this.addedSignature && this.addedSignature === this.stateSignature(state)) {
        message = this.isFixed ? 'Bundle – Added to cart' : `${state.quantity} Refills – Added to cart`;
      } else if (this.isFixed) {
        const needed = this.requiredQuantity - state.quantity;
        if (needed <= 0) message = 'Add to cart';
        else if (state.quantity === 0) message = `Choose ${needed} ${this.pluralize(needed)}`;
        else message = `Choose ${needed} more ${this.pluralize(needed)}`;
      } else if (state.quantity === 0) {
        message = 'Add 5 to Purchase';
      } else {
        message = `${state.quantity} ${this.pluralize(state.quantity)} – Add to cart`;
      }

      if (this.isFixed) {
        tierMessage = `${state.quantity} of ${this.requiredQuantity} refills selected`;
      } else if (state.discount === 0) {
        tierMessage = `Add ${remaining} more ${remaining === 1 ? 'refill' : 'refills'} to unlock 5% off`;
      } else if (!nextTier) {
        tierMessage = '20% off unlocked!';
      } else {
        tierMessage = `${state.discount}% off unlocked! Add ${remaining} more to save ${nextTier}%`;
      }

      if (text) text.textContent = message;
      if (progressMessage) progressMessage.textContent = tierMessage;
      if (liveStatus) liveStatus.textContent = `${tierMessage}. ${message}`;
      if (progress) {
        const progressPercent = Math.min((state.quantity / this.maxItems) * 100, 100);
        progress.style.width = state.quantity > 0 ? `${progressPercent}%` : '0%';
        progress.style.background = this.tierColorFor(state.quantity, tiers);
      }
      if (progressTrack) progressTrack.setAttribute('aria-valuenow', String(state.quantity));
      this.querySelectorAll('[data-discount-tier]').forEach((label) => {
        const reached = Number(label.dataset.discountTier) <= state.quantity;
        label.classList.toggle('is-reached', reached);
      });
      if (discountBadge) {
        discountBadge.textContent = `${state.discount}% off`;
        discountBadge.hidden = state.discount === 0;
      }
      if (this.cta) {
        this.cta.disabled = !eligible || this.isSubmitting;
        this.cta.classList.toggle('is-loading', this.isSubmitting);
        this.cta.setAttribute('aria-disabled', String(!eligible || this.isSubmitting));
        this.cta.setAttribute('aria-label', eligible
          ? (this.isFixed ? 'Add bundle to cart' : `Add ${state.quantity} selected refills to cart`)
          : message);
      }
    }

    // Fixed bundles never show tier progress or tier discounts. Otherwise the
    // fill keeps its default styling below the first tier and takes the color of
    // the latest reached tier, so reducing the quantity restores the earlier one.
    tierColorFor(quantity, tiers) {
      if (this.isFixed) return '';
      let reachedIndex = -1;
      tiers.forEach((tier, index) => {
        if (quantity >= tier) reachedIndex = index;
      });
      if (reachedIndex < 0) return '';
      return (this.tierColors && this.tierColors[reachedIndex]) || '';
    }

    async addToCart(state) {
      if (this.isSubmitting || state.quantity < this.minItems) return;

      if (!this.flexBundleVariantId) {
        this.setCartMessage('Select a Flex bundle product in the theme editor before this bundle can be added to the cart.', true);
        return;
      }

      const missingItems = state.items.filter((item) => !item.variantId);
      if (missingItems.length > 0) {
        this.setCartMessage(`Assign a Shopify product to: ${missingItems.map((item) => item.title).join(', ')}.`, true);
        return;
      }

      this.isSubmitting = true;
      this.clearAddedState();
      this.renderCta(state);

      try {
        const cartAddUrl = window.theme && window.theme.routes && window.theme.routes.cartAdd
          ? window.theme.routes.cartAdd
          : '/cart/add.js';
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: [this.buildFlexBundleItem(state)]
          })
        });
        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.description || result.message || 'The bundle could not be added to the cart.');
        }

        this.addedSignature = this.stateSignature(state);
        this.setCartMessage(this.isFixed
          ? 'Your bundle was added to your cart.'
          : `${state.quantity} selected refills were added to your cart.`);
        window.clearTimeout(this.addedTimer);
        this.addedTimer = window.setTimeout(() => {
          this.clearAddedState();
          this.renderCta(this.getState());
        }, 4000);
        document.dispatchEvent(new CustomEvent('addedToCart'));
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: {
            product: result,
            addToCartBtn: this.cta
          }
        }));

        if (!document.querySelector('#CartDrawer')) {
          window.location.assign('/cart');
        }
      } catch (error) {
        this.setCartMessage(error.message || 'The bundle could not be added to the cart.', true);
      } finally {
        this.isSubmitting = false;
        this.renderCta(this.getState());
      }
    }

    // Builds a Flex Bundles "Component Sum" line item: the parent bundle variant
    // is the cart line, and every selected refill is passed in `_components`
    // with its discounted unit price. The tier discount is recorded on each
    // component's attributes so it travels with the component in the cart.
    buildFlexBundleItem(state) {
      if (this.isFixed) return this.buildFixedBundleItem(state);

      const discountLabel = `${state.discount}%`;
      const components = state.items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
        price: this.toDecimal(item.discountedPrice),
        attributes: {
          'Color': item.title,
          'Discount': discountLabel,
          'Original price': this.formatMoney(item.price),
          'Discounted price': this.formatMoney(item.discountedPrice)
        }
      }));

      const properties = {
        '_components': JSON.stringify(components),
        // Hidden, Liquid-parseable summary used by snippets/cart-item.liquid to
        // render a swatch list: title|quantity|color|color_two|image;title|...
        '_tiy_refills': state.items
          .map((item) => [item.title, item.quantity, item.color, item.colorTwo, item.swatchImage]
            .map((value) => String(value).replace(/[|;]/g, ' '))
            .join('|'))
          .join(';'),
        'Discount': `${discountLabel} off`
      };

      const currency = window.Shopify && window.Shopify.currency;
      if (currency && currency.active) {
        properties['_currency'] = currency.active;
        properties['_currency_rate'] = String(currency.rate || '1.0');
      }

      return {
        id: this.flexBundleVariantId,
        quantity: 1,
        properties
      };
    }

    // Fixed bundles are priced by the parent product, so components carry no
    // price. The included product (e.g. the cutting case) always goes first.
    buildFixedBundleItem(state) {
      const components = state.items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
        attributes: {
          'Color': item.title
        }
      }));
      const summary = state.items.map((item) => [item.title, item.quantity, item.color, item.colorTwo, item.swatchImage]);

      if (this.includedVariantId) {
        components.unshift({ id: this.includedVariantId, quantity: 1 });
        summary.unshift([this.includedTitle, 1, '#ddd', '#ddd', this.includedImage]);
      }

      return {
        id: this.flexBundleVariantId,
        quantity: 1,
        properties: {
          '_components': JSON.stringify(components),
          '_tiy_refills': summary
            .map((entry) => entry.map((value) => String(value).replace(/[|;]/g, ' ')).join('|'))
            .join(';')
        }
      };
    }

    toDecimal(cents) {
      return Number(((Number(cents) || 0) / 100).toFixed(2));
    }

    setCartMessage(message, isError = false) {
      if (!this.cartMessage) return;
      this.cartMessage.textContent = message;
      this.cartMessage.hidden = !message;
      this.cartMessage.classList.toggle('is-error', Boolean(message) && isError);
    }

    stateSignature(state) {
      return state.items.map((item) => `${item.key}:${item.quantity}`).sort().join('|');
    }

    setupScrollCue(rail, direction) {
      if (!rail) return;

      const update = () => {
        const vertical = direction === 'vertical';
        const position = vertical ? rail.scrollTop : rail.scrollLeft;
        const scrollSize = vertical ? rail.scrollHeight : rail.scrollWidth;
        const clientSize = vertical ? rail.clientHeight : rail.clientWidth;
        const remaining = Math.max(0, scrollSize - clientSize - position);

        rail.classList.toggle('has-scroll-before', position > 2);
        rail.classList.toggle('has-scroll-after', remaining > 2);
      };

      rail.addEventListener('scroll', update, { passive: true });
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(update);
        observer.observe(rail);
        this.scrollCueObservers.push(observer);
      }
      this.scrollCueUpdates.push(update);
      requestAnimationFrame(update);
    }

    updateScrollCues() {
      if (!this.scrollCueUpdates) return;
      requestAnimationFrame(() => this.scrollCueUpdates.forEach((update) => update()));
    }

    // The bundle owns its swatch names so they still appear on themes that load
    // only this section's assets. assets/custom.js explicitly skips the marked
    // bundle swatches, so the shared PDP tooltip never renders a duplicate.
    setupSwatchTooltips() {
      // Bind once so repeated calls register the exact same handler references.
      // The DOM ignores a duplicate (type, callback, capture) registration, which
      // keeps this idempotent for reconnect and repeated setup.
      if (!this.swatchTooltipHandlersBound) {
        this.swatchTooltipHandlersBound = true;
        this.onSwatchPointerOver = this.onSwatchPointerOver.bind(this);
        this.onSwatchPointerOut = this.onSwatchPointerOut.bind(this);
        this.onSwatchFocusIn = this.onSwatchFocusIn.bind(this);
        this.onSwatchFocusOut = this.onSwatchFocusOut.bind(this);
        this.onSwatchTooltipViewportChange = this.positionSwatchTooltip.bind(this);
      }
      this.addEventListener('pointerover', this.onSwatchPointerOver);
      this.addEventListener('pointerout', this.onSwatchPointerOut);
      this.addEventListener('focusin', this.onSwatchFocusIn);
      this.addEventListener('focusout', this.onSwatchFocusOut);
      window.addEventListener('scroll', this.onSwatchTooltipViewportChange, true);
      window.addEventListener('resize', this.onSwatchTooltipViewportChange);
    }

    teardownSwatchTooltips() {
      if (this.onSwatchPointerOver) this.removeEventListener('pointerover', this.onSwatchPointerOver);
      if (this.onSwatchPointerOut) this.removeEventListener('pointerout', this.onSwatchPointerOut);
      if (this.onSwatchFocusIn) this.removeEventListener('focusin', this.onSwatchFocusIn);
      if (this.onSwatchFocusOut) this.removeEventListener('focusout', this.onSwatchFocusOut);
      if (this.onSwatchTooltipViewportChange) {
        window.removeEventListener('scroll', this.onSwatchTooltipViewportChange, true);
        window.removeEventListener('resize', this.onSwatchTooltipViewportChange);
      }
      this.hideSwatchTooltip();
      if (this.swatchTooltip && this.swatchTooltip.parentNode) {
        this.swatchTooltip.parentNode.removeChild(this.swatchTooltip);
      }
      this.swatchTooltip = null;
    }

    getSwatchFromTarget(target) {
      if (!target || typeof target.closest !== 'function') return null;
      return target.closest('[data-swatch]');
    }

    onSwatchPointerOver(event) {
      if (event.pointerType === 'touch') return;
      const swatch = this.getSwatchFromTarget(event.target);
      if (!swatch || swatch.disabled) return;
      if (event.relatedTarget && typeof swatch.contains === 'function' && swatch.contains(event.relatedTarget)) return;
      this.showSwatchTooltip(swatch);
    }

    onSwatchPointerOut(event) {
      const swatch = this.getSwatchFromTarget(event.target);
      if (!swatch) return;
      if (event.relatedTarget && typeof swatch.contains === 'function' && swatch.contains(event.relatedTarget)) return;
      this.hideSwatchTooltip(swatch);
    }

    onSwatchFocusIn(event) {
      const swatch = this.getSwatchFromTarget(event.target);
      if (!swatch) return;
      let focusVisible = true;
      try {
        if (typeof event.target.matches === 'function') focusVisible = event.target.matches(':focus-visible');
      } catch (error) {
        focusVisible = true;
      }
      if (!focusVisible) return;
      this.showSwatchTooltip(swatch);
    }

    onSwatchFocusOut(event) {
      const swatch = this.getSwatchFromTarget(event.target);
      // Focus can move between non-swatch controls (family tabs, quantity
      // buttons) while a swatch tooltip is showing. Only a swatch losing focus
      // clears its own name.
      if (!swatch) return;
      this.hideSwatchTooltip(swatch);
    }

    getSwatchTooltip() {
      // A detached node can linger after an external DOM move, so replace it
      // rather than showing a tooltip that is no longer in the document.
      if (this.swatchTooltip && this.swatchTooltip.isConnected !== false) return this.swatchTooltip;
      const tooltip = document.createElement('div');
      tooltip.className = 'tiy-bundle__swatch-tooltip';
      tooltip.setAttribute('role', 'tooltip');
      tooltip.setAttribute('aria-hidden', 'true');
      tooltip.hidden = true;
      const scope = this.id || `builder-${this.dataset.sectionId || ++swatchTooltipSequence}`;
      tooltip.id = `TiyBundleSwatchTooltip-${scope}`;
      if (document.body) document.body.appendChild(tooltip);
      this.swatchTooltip = tooltip;
      return tooltip;
    }

    showSwatchTooltip(swatch) {
      const text = swatch && swatch.dataset && typeof swatch.dataset.swatchTooltip === 'string'
        ? swatch.dataset.swatchTooltip.trim()
        : '';
      if (!text) return;
      const tooltip = this.getSwatchTooltip();
      this.swatchTooltipAnchor = swatch;
      tooltip.textContent = text;
      tooltip.hidden = false;
      tooltip.setAttribute('aria-hidden', 'false');
      this.positionSwatchTooltip();
    }

    hideSwatchTooltip(swatch) {
      if (!this.swatchTooltip || (swatch && swatch !== this.swatchTooltipAnchor)) return;
      this.swatchTooltip.hidden = true;
      this.swatchTooltip.setAttribute('aria-hidden', 'true');
      this.swatchTooltipAnchor = null;
    }

    isSwatchTooltipAnchorVisible() {
      const anchor = this.swatchTooltipAnchor;
      if (!anchor || typeof anchor.getBoundingClientRect !== 'function') return true;
      const rect = anchor.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return false;
      const viewportWidth = Number(window.innerWidth) || 0;
      const viewportHeight = Number(window.innerHeight) || 0;
      if (viewportWidth && viewportHeight
        && (rect.bottom < 0 || rect.top > viewportHeight || rect.right < 0 || rect.left > viewportWidth)) {
        return false;
      }
      const rail = typeof anchor.closest === 'function' ? anchor.closest('.tiy-bundle__swatches') : null;
      if (rail && typeof rail.getBoundingClientRect === 'function') {
        const railRect = rail.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        if (railRect.width && (centerX < railRect.left || centerX > railRect.right)) return false;
      }
      return true;
    }

    positionSwatchTooltip() {
      if (!this.swatchTooltip ||
        !this.swatchTooltipAnchor ||
        this.swatchTooltip.hidden ||
        typeof this.swatchTooltipAnchor.getBoundingClientRect !== 'function') return;
      if (!this.isSwatchTooltipAnchorVisible()) {
        this.hideSwatchTooltip();
        return;
      }

      const viewportPadding = 8;
      const tooltipGap = 8;
      const anchorRect = this.swatchTooltipAnchor.getBoundingClientRect();
      const tooltipRect = this.swatchTooltip.getBoundingClientRect();
      const viewportWidth = Number(window.innerWidth) || 0;
      const viewportHeight = Number(window.innerHeight) || 0;

      let top = anchorRect.top - tooltipRect.height - tooltipGap;
      if (top < viewportPadding) top = anchorRect.bottom + tooltipGap;
      if (viewportHeight) top = Math.min(top, viewportHeight - tooltipRect.height - viewportPadding);
      top = Math.max(viewportPadding, top);

      let left = anchorRect.left + (anchorRect.width - tooltipRect.width) / 2;
      if (viewportWidth) {
        left = Math.min(left, viewportWidth - tooltipRect.width - viewportPadding);
      }
      left = Math.max(viewportPadding, left);

      this.swatchTooltip.style.left = `${Math.round(left)}px`;
      this.swatchTooltip.style.top = `${Math.round(top)}px`;
    }

    getState() {
      const rawItems = this.rows.reduce((selected, row) => {
        const quantity = this.quantities.get(row.dataset.selectedRow) || 0;
        if (quantity > 0) {
          selected.push({
            key: row.dataset.selectedRow,
            productId: row.dataset.productId || null,
            variantId: row.dataset.variantId || null,
            title: row.dataset.title,
            price: Number(row.dataset.price) || 0,
            color: row.dataset.color || '',
            colorTwo: row.dataset.colorTwo || row.dataset.color || '',
            swatchImage: row.dataset.swatchImage || '',
            quantity
          });
        }
        return selected;
      }, []);
      const quantity = rawItems.reduce((sum, item) => sum + item.quantity, 0);
      const discount = this.isFixed ? 0 : quantity >= 20 ? 20 : quantity >= 15 ? 15 : quantity >= 10 ? 10 : quantity >= 5 ? 5 : 0;
      // Discount is applied per unit (rounded to the cent) so the total shown
      // on the page matches the sum of the component prices sent to the cart.
      const items = rawItems.map((item) => ({
        ...item,
        discount,
        discountedPrice: Math.round(item.price * (100 - discount) / 100)
      }));
      const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      const total = this.isFixed
        ? this.basePrice
        : items.reduce((sum, item) => sum + (item.discountedPrice * item.quantity), 0);

      return {
        items,
        quantity,
        discount,
        subtotal,
        total,
        maxItems: this.maxItems
      };
    }

    getTotalQuantity() {
      return Array.from(this.quantities.values()).reduce((sum, quantity) => sum + quantity, 0);
    }

    // Automatic catalogues render one server page and let the builder pull the
    // rest through same-origin section rendering, using the platform's own page
    // parameter name, current page and page count.
    initialiseCatalogue() {
      this.catalogueMode = this.dataset.catalogueMode === 'collection';
      this.cataloguePages = Math.max(1, Number(this.dataset.cataloguePages) || 1);
      this.catalogueCurrentPage = Math.max(1, Number(this.dataset.catalogueCurrentPage) || 1);
      this.cataloguePageParam = this.dataset.cataloguePageParam || 'page';
      this.catalogueSource = this.dataset.catalogueSource || '';
      this.catalogueSwatchAnchor = this.swatches[0] || null;
      this.catalogueRowAnchor = this.rows[0] || null;
      this.cataloguePlan = [];
      if (this.catalogueMode && this.cataloguePages > 1) {
        for (let page = 1; page <= this.cataloguePages; page += 1) {
          // Ascending, so pages before the server-rendered one are inserted in
          // order ahead of it and later pages append in order. A deep-link into
          // page 3 therefore still ends up with page 1 and 2 present.
          if (page !== this.catalogueCurrentPage) this.cataloguePlan.push(page);
        }
      }
      this.cataloguePlanIndex = 0;
      this.catalogueLoading = false;
      this.catalogueLoadToken = 0;
    }

    // Anything already in flight is ignored when it resolves, and the next
    // connect starts a fresh pass from the first page that never landed.
    abortCatalogueLoad() {
      if (!this.catalogueMode) return;
      this.catalogueLoadToken += 1;
      this.catalogueLoading = false;
    }

    resumeCatalogueLoad() {
      if (!this.catalogueMode) return;
      if (!this.cataloguePlan || this.cataloguePlanIndex >= this.cataloguePlan.length) return;
      // Remembered so a caller (theme script, test) can await the full
      // catalogue instead of guessing when the extra pages arrived.
      this.catalogueLoadPromise = this.loadCataloguePages();
    }

    // Same-origin path only, keeping the page's existing query string
    // (preview_theme_id and friends) and replacing just section_id and the
    // platform's own page parameter, so no product data can steer the request.
    catalogueUrl(page) {
      const path = window.location && window.location.pathname ? window.location.pathname : '/';
      const params = new URLSearchParams(window.location.search || '');
      params.set('section_id', this.dataset.sectionId || '');
      params.set(this.cataloguePageParam, String(page));
      return `${path}?${params.toString()}`;
    }

    setCatalogueStatus(message, isError = false) {
      const status = this.querySelector('[data-catalogue-status]');
      const wrap = this.querySelector('[data-catalogue-more-wrap]');
      if (status) {
        status.textContent = message;
        status.hidden = !message;
        status.classList.toggle('is-error', Boolean(message) && isError);
      }
      if (wrap && message) wrap.hidden = false;
    }

    async loadCataloguePages() {
      if (!this.catalogueMode || this.catalogueLoading) return;
      if (this.cataloguePlanIndex >= this.cataloguePlan.length) return;

      const token = this.catalogueLoadToken;
      const more = this.querySelector('[data-catalogue-more]');
      const label = this.querySelector('[data-catalogue-more-label]');
      this.catalogueLoading = true;
      if (more) {
        more.disabled = true;
        more.classList.add('is-loading');
      }

      try {
        while (this.cataloguePlanIndex < this.cataloguePlan.length) {
          const page = this.cataloguePlan[this.cataloguePlanIndex];
          this.setCatalogueStatus(`Loading refills (page ${page} of ${this.cataloguePages})…`);
          const builder = await this.loadCataloguePage(page);
          // A disconnect during the fetch drops the response instead of
          // painting into a builder that is no longer in the document.
          if (token !== this.catalogueLoadToken) return;
          // A page whose products are all already rendered adds nothing, but it
          // never ends the run: later pages can still carry new refills.
          this.appendCataloguePage(builder, page);
          this.cataloguePlanIndex += 1;
        }
        this.setCatalogueStatus('');
        const wrap = this.querySelector('[data-catalogue-more-wrap]');
        if (wrap) wrap.hidden = true;
      } catch (error) {
        if (token !== this.catalogueLoadToken) return;
        this.setCatalogueStatus(
          (error && error.message) || 'Refills could not be loaded.',
          true
        );
        if (label) label.textContent = 'Retry loading refills';
      } finally {
        if (token === this.catalogueLoadToken) {
          this.catalogueLoading = false;
          if (more) {
            more.disabled = false;
            more.classList.remove('is-loading');
          }
        }
      }
    }

    async loadCataloguePage(page) {
      const response = await fetch(this.catalogueUrl(page), {
        credentials: 'same-origin',
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      if (!response.ok) throw new Error(`Refills could not be loaded (${response.status}).`);

      const markup = await response.text();
      if (!markup || markup.trim() === '') throw new Error('Refills could not be loaded.');

      // DOMParser always returns a document, even for an error page, so the
      // markup itself has to prove it is this builder's page.
      const doc = this.parseCatalogueDocument(markup);
      const builder = this.selectCatalogueBuilder(doc);
      this.assertCatalogueBuilder(builder, page);
      return builder;
    }

    parseCatalogueDocument(markup) {
      const Parser = (window && window.DOMParser) || (typeof DOMParser !== 'undefined' ? DOMParser : null);
      if (!Parser) return null;
      return new Parser().parseFromString(markup, 'text/html');
    }

    selectCatalogueBuilder(doc) {
      if (!doc || typeof doc.querySelectorAll !== 'function') return null;
      const builders = Array.from(doc.querySelectorAll('tiy-bundle-builder'));
      const wantedId = this.id;
      const wantedSection = this.dataset.sectionId || '';
      const matches = builders.filter((builder) => {
        if (!builder || !builder.dataset) return false;
        // Every identity the builder knows about has to agree, so a response
        // for a different section instance can never be merged in.
        if (wantedId && builder.id !== wantedId) return false;
        if (wantedSection && builder.dataset.sectionId !== wantedSection) return false;
        return Boolean(wantedId || wantedSection);
      });
      return matches.length === 1 ? matches[0] : null;
    }

    assertCatalogueBuilder(builder, page) {
      const fail = () => new Error('Refills could not be loaded (unexpected response).');
      if (!builder || !builder.dataset) throw fail();
      if (builder.dataset.catalogueMode !== 'collection') throw fail();
      if (!this.catalogueSource || builder.dataset.catalogueSource !== this.catalogueSource) throw fail();
      if (builder.dataset.cataloguePageParam !== this.cataloguePageParam) throw fail();
      const returnedPage = Number(builder.dataset.catalogueCurrentPage);
      if (!Number.isInteger(returnedPage) || returnedPage !== page) throw fail();
    }

    // Merges one validated page. Only complete swatch/row pairs that are not
    // already rendered are taken, identity is the product id, existing
    // quantities are never touched, and the builder re-renders afterwards so
    // the cap, row increments, price, CTA and scroll cues stay in sync.
    appendCataloguePage(builder, page) {
      const rail = this.querySelector('.tiy-bundle__swatches');
      const selection = this.querySelector('[data-selection-list]');
      if (!rail || !selection || !builder || typeof builder.querySelectorAll !== 'function') return 0;

      const swatchNodes = Array.from(builder.querySelectorAll('.tiy-bundle__swatches [data-swatch]'));
      const rowNodes = Array.from(builder.querySelectorAll('[data-selection-list] [data-selected-row]'));
      const rowsByKey = new Map();
      rowNodes.forEach((row) => rowsByKey.set(row.dataset.selectedRow, row));

      const knownKeys = new Set(this.swatches.map((swatch) => swatch.dataset.itemKey));
      const knownRows = new Set(this.rows.map((row) => row.dataset.selectedRow));
      const knownProducts = new Set(
        this.swatches.map((swatch) => swatch.dataset.productId).filter(Boolean)
      );
      // Pages before the server-rendered one are inserted ahead of the first
      // rendered item so the catalogue still reads in collection order.
      const insertBefore = page < this.catalogueCurrentPage;
      let added = 0;

      swatchNodes.forEach((swatch) => {
        const key = swatch.dataset.itemKey;
        const productId = swatch.dataset.productId;
        const row = rowsByKey.get(key);
        // Every catalogue item is a swatch plus its row, and a product that is
        // already rendered never comes back, however the page shifts.
        if (!key || !productId || !row) return;
        if (row.dataset.productId !== productId || row.dataset.variantId !== swatch.dataset.variantId) return;
        if (knownKeys.has(key) || knownRows.has(key) || knownProducts.has(productId)) return;
        knownKeys.add(key);
        knownRows.add(key);
        knownProducts.add(productId);
        this.insertCatalogueNode(rail, swatch, insertBefore ? this.catalogueSwatchAnchor : null);
        row.hidden = true;
        this.insertCatalogueNode(selection, row, insertBefore ? this.catalogueRowAnchor : null);
        this.swatches.push(swatch);
        this.rows.push(row);
        this.quantities.set(key, 0);
        added += 1;
      });

      this.refreshCatalogueCounts();
      this.syncCatalogueFeedback();
      this.render(false);
      return added;
    }

    insertCatalogueNode(parent, node, anchor) {
      if (anchor && anchor.parentNode === parent && typeof parent.insertBefore === 'function') {
        parent.insertBefore(node, anchor);
        return;
      }
      parent.appendChild(node);
    }

    refreshCatalogueCounts() {
      if (!this.catalogueMode) return;
      const counts = new Map();
      this.swatches.forEach((swatch) => {
        const family = swatch.dataset.family;
        if (!family) return;
        counts.set(family, (counts.get(family) || 0) + 1);
      });
      this.querySelectorAll('[data-family-tab]').forEach((tab) => {
        const key = tab.dataset.familyTab;
        const value = key === 'all' ? this.swatches.length : (counts.get(key) || 0);
        tab.dataset.familyCount = String(value);
      });
      const active = this.querySelector('[data-family-tab][aria-selected="true"]');
      if (active) this.selectFamily(active, false);
    }

    syncCatalogueFeedback() {
      if (!this.catalogueMode) return;
      const note = this.querySelector('[data-catalogue-feedback]');
      if (!note) return;
      note.hidden = this.swatches.length > 0;
    }

    pluralize(quantity) {
      return quantity === 1 ? 'Refill' : 'Refills';
    }

    formatMoney(cents) {
      const value = Number(cents) || 0;
      const amount = (value / 100).toFixed(2);
      const amountNoDecimals = Math.round(value / 100).toString();
      return this.moneyFormat
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, amountNoDecimals)
        .replace(/\{\{\s*amount\s*\}\}/, amount);
    }

    enableDragScroll(rail) {
      let startX = 0;
      let startScroll = 0;
      let moved = false;
      let pointerId = null;

      rail.addEventListener('pointerdown', (event) => {
        // Touch devices already provide native horizontal scrolling. Custom
        // dragging is only needed for a mouse, where capturing too early would
        // retarget a normal button click to the rail itself.
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        pointerId = event.pointerId;
        startX = event.clientX;
        startScroll = rail.scrollLeft;
        moved = false;
      });

      rail.addEventListener('pointermove', (event) => {
        if (pointerId !== event.pointerId) return;
        const distance = event.clientX - startX;
        if (Math.abs(distance) > 4 && !moved) {
          moved = true;
          rail.setPointerCapture(event.pointerId);
          rail.classList.add('is-dragging');
        }
        if (moved) {
          event.preventDefault();
          rail.scrollLeft = startScroll - distance;
        }
      });

      const release = (event) => {
        if (pointerId !== event.pointerId) return;
        if (rail.hasPointerCapture(event.pointerId)) rail.releasePointerCapture(event.pointerId);
        pointerId = null;
        window.setTimeout(() => rail.classList.remove('is-dragging'), 0);
      };
      rail.addEventListener('pointerup', release);
      rail.addEventListener('pointercancel', release);
      rail.addEventListener('click', (event) => {
        if (moved) {
          event.preventDefault();
          event.stopPropagation();
          moved = false;
        }
      }, true);
    }
  }

  if (!customElements.get('tiy-bundle-builder')) {
    customElements.define('tiy-bundle-builder', TiyBundleBuilder);
  }
})();
